import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CommentItem from './CommentItem';
import AddCommentForm from './AddCommentForm';
import { Comment, CommentCreate, CommentUpdate } from '../../../types';
import { Button } from '../../Common/button';

interface DiscussionTabProps {
  comments: Comment[];
  isLoading: boolean;
  error: string | null;
  createComment: (data: CommentCreate) => Promise<Comment | null>;
  updateComment: (commentId: string, data: CommentUpdate) => Promise<Comment | null>;
  deleteComment: (commentId: string) => Promise<boolean>;
}

const DiscussionTab: React.FC<DiscussionTabProps> = ({
  comments,
  isLoading,
  error,
  createComment,
  updateComment,
  deleteComment,
}) => {
  const { t } = useTranslation();
  const [showAddForm, setShowAddForm] = useState(false);

  const handleAddComment = async (content: string) => {
    // The parent hook (useUseCaseDiscussion) will handle loading state via the isLoading prop
    try {
      await createComment({ content });
      setShowAddForm(false);
    } catch (err) {
      console.error("DiscussionTab: Failed to add comment", err);
    }
  };

  const handleUpdateComment = async (commentId: string, newContent: string) => {
     if (!window.confirm(t('useCaseDiscussion.updateConfirm', { id: commentId }))) return; // Simple confirmation
     try {
        await updateComment(commentId, { content: newContent });
        // Hook will refetch/update state
     } catch (err) {
        console.error(`DiscussionTab: Failed to update comment ${commentId}`, err);
     }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm(t('useCaseDiscussion.deleteConfirm', { id: commentId }))) return;
    try {
        await deleteComment(commentId);
    } catch (err) {
        console.error(`DiscussionTab: Failed to delete comment ${commentId}`, err);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-6">
      <h2 className="text-xl font-semibold text-foreground border-b border-border pb-2">{t('useCaseDiscussion.title')}</h2>

      {/* Display Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-400 text-red-600 px-4 py-3 rounded-md" role="alert">
          <strong className="font-semibold">{t('useCaseDiscussion.errorTitle')}</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      )}

      {/* Display Loading State */}
      {isLoading && <p className="text-center text-muted-foreground">{t('useCaseDiscussion.loadingComments')}</p>}

      {/* List of Comments */}
      {!isLoading && ( // Render list only when not loading
        <div className="space-y-4">
          {comments.length > 0 ? (
            comments.map((comment, index) => {
              if (!comment || !comment.id) {
                console.error(`Invalid comment data at index ${index}:`, comment);
                return <div key={`error-${index}`} className="text-red-500 text-xs">{t('useCaseDiscussion.invalidData', { index })}</div>;
              }
              return (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                // Pass update/delete handlers to CommentItem
                onUpdate={handleUpdateComment} // Pass the handler
                onDelete={handleDeleteComment} // Pass the handler
                />
              );
            })
          ) : (
            <p className="text-sm text-muted-foreground italic">{t('useCaseDiscussion.noComments')}</p>
          )}
        </div>
      )}

      {!showAddForm ? (
        <Button type="button" size="sm" onClick={() => setShowAddForm(true)}>
          {t('useCaseDiscussion.sendNewMessage')}
        </Button>
      ) : (
        <AddCommentForm onSubmit={handleAddComment} isLoading={isLoading} onCancel={() => setShowAddForm(false)} />
      )}
    </div>
  );
};

export default DiscussionTab;
